import { Component, HostBinding, OnChanges, OnDestroy } from '@angular/core';
import { IonItemSliding } from '@ionic/angular';
import { Input, Output, EventEmitter } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';

import { Subscription } from 'rxjs';

import { PlacePanel } from '@unpispas/upp-base';
import { PriceType } from '@unpispas/upp-defs';
import { ViewRenderer } from '@unpispas/upp-wdgt';

import { alertService } from '@unpispas/upp-base';
import { languageService } from '@unpispas/upp-base';
import { i18nSupport } from '@unpispas/upp-data';
import { dataService } from '@unpispas/upp-data';
import { viewService } from '@unpispas/upp-base';

import { TicketView } from '@unpispas/upp-data';
import { TicketOption } from '@unpispas/upp-data';
import { TicketUnits } from '@unpispas/upp-data';
import { TicketProductView } from '@unpispas/upp-data';
import { ProductView } from '@unpispas/upp-data';
import { ProductOptView } from '@unpispas/upp-data';
import { PreselectView } from '@unpispas/upp-data';
import { OfferView } from '@unpispas/upp-data';

export type TicketProductStatus = 'AC' | 'PD' | 'PP' | 'RD' | null;

/****************************/
/* TICKETPRODUCT DISPLAY    */
/****************************/

export class TicketProductDisplay {
    constructor(
        private ticketproduct: TicketProductView,
        private change: ViewRenderer,
        private data: dataService,
        private _i18n: i18nSupport,
    ){
        // nothing to do
    }

    markForCheck(): void {
        this._product = undefined;
        this._shortcut = undefined;

        this.change.markForCheck();
    }

    private _expand = false;
    get expand(): boolean {
        return this._expand;
    }

    set expand(value: boolean){
        this._expand = value;
    }

    /* access to data members */

    private _shortcut: PreselectView | null | undefined = undefined;
    private get shortcut(): PreselectView | null {
        if (this._shortcut == undefined){
            const product = this.ticketproduct.product;
            if (!product){
                return null;
            }
    
            const selects: Array<PreselectView> = product.selects.slice(0).sort((a, b) => {
                const _aopts = a.options.length;
                const _bopts = b.options.length;
    
                return (_bopts - _aopts);   // sort by number of options desc
            });
    
            let shortcut = null;
    
            for (const select of selects){
                if (this.ticketproduct.IsDirect(select)){
                    shortcut = select;  // this is the match with more options
                }
    
                if (shortcut){
                    break;  // this is a shortcut
                }
            }
    
            this._shortcut = shortcut;
        }

        return this._shortcut;
    }

    private _product: ProductView | null | undefined = undefined;
    private get product(): ProductView | null {
        if (this._product === undefined){
            if (this.shortcut){
                return null;
            }
    
            this._product = this.ticketproduct.product;    
        }

        return this._product;
    }

    /* access to calculated values */

    get ticket(): TicketView | null {
        return this.ticketproduct.ticket;
    }

    get photo() : string | null {
        return (this.shortcut?.photo) || (this.product?.photo) || null;
    }

    get avatar() : string | null {
        return (this.shortcut?.avatar) || (this.product?.avatar) || null;
    }

    get name(): string | null {
        if (this.shortcut){
            return this._i18n.tr(this.shortcut.name);
        }

        if (this.product){
            return this._i18n.tr(this.product.name);
        }

        return null;
    }

    get main(): string | null {
        const _product = this.ticketproduct.product;
        if (!_product){
            return null;
        }

        return this._i18n.tr(_product.name);
    }

    get amount(): number {
        let _amount: number = this.ticketproduct.amount || 0;

        let _target = this.ticketproduct.DisplayFr;
        while (_target !== null){
            _amount += _target.amount || 0;
            _target = _target.DisplayFr;
        }

        return _amount;
    }

    get comments(): string | null {
        return this.ticketproduct.comments || null;
    }

    get weighted(): boolean {
        if (this.ticketproduct.product){
            return this.ticketproduct.product.IsWeighted;
        }
        return false;
    }

    get weight(): number | null {
        if (this.weighted){
            return this.ticketproduct.weight;
        }
        return null;
    }

    get available(): boolean {
        return (this.shortcut?.IsAvailable) || (this.product?.IsAvailable) || false;
    }

    private get unitprice(): number {
        const _custom = this.ticketproduct.product?.IsGeneric || this.ticketproduct.product?.IsVarious;
        if (_custom){
            const fixed = this.ticketproduct.calcinfo.fixed;
            if (fixed !== null){
                return fixed;
            }

            return this.ticketproduct.calcinfo.charge || 0;
        }

        return this.ticketproduct.calcinfo.price || 0;
    }

    get charge(): string | null {
        return (Math.round(this.unitprice * 100)/100).toFixed(2);
    }

    get options(): Array <TicketOptionDisplay> {
        const _options: Array <TicketOptionDisplay> = [];
        for(const option of this.ticketproduct.ticketoptions){
            _options.push(new TicketOptionDisplay(option, this.change, this.data, this._i18n))
        }    
        return _options;
    }

    get price(): PriceType | null {
        if (this.ticketproduct.product){
            return this.ticketproduct.product.priceinfo;
        }

        return null;
    }

    get reprice(): boolean {
        return this.isfixed || (this.inoffer != null);
    }

    get isfixed(): boolean {
        if (this.ticketproduct.product?.IsGeneric || this.ticketproduct.product?.IsVarious){
            return false;
        }

        return this.ticketproduct.fixed || false;
    }

    get isinvite(): boolean {
        if (this.ticketproduct.product?.IsGeneric || this.ticketproduct.product?.IsVarious){
            return false;
        }

        return this.ticketproduct.IsInvite;
    }

    get onthehouse(): boolean {
        if (!this.isfixed){
            return false;
        }

        if (this.ticketproduct.calcinfo && this.ticketproduct.calcinfo.fixed){
            return (this.ticketproduct.calcinfo.fixed == 0);
        }

        return false;        
    }

    get inoffer(): OfferView | null {
        if (this.ticketproduct.offer){
            return this.ticketproduct.offer.offer;
        }

        return null;
    }

    get totalprice(): string | null {
        if (this.ticketproduct.calcinfo && (this.ticketproduct.calcinfo.price !== null)){
            return (Math.round((this.amount * this.unitprice) * 100)/100).toFixed(2);
        }

        return null;
    }

    get totalfixed(): string | null {
        if (this.ticketproduct.calcinfo && (this.ticketproduct.calcinfo.fixed !== null)){
            return (Math.round((this.amount * this.ticketproduct.calcinfo.fixed) * 100)/100).toFixed(2);
        }

        return null;
    }

    get totaloffer(): string | null {
        if (this.ticketproduct.calcinfo && (this.ticketproduct.calcinfo.final !== null)){
            return (Math.round((this.amount * this.ticketproduct.calcinfo.final) * 100)/100).toFixed(2);
        }

        return null;
    }

    get paidstatus(): TicketProductStatus {
        return null;  // TODO
    }

    get donestatus(): TicketProductStatus {
        return null;  // TODO
    }
}

/****************************/
/* TICKETOPTION DISPLAY     */
/****************************/

export class TicketOptionDisplay {
    constructor(
        private ticketoption: TicketOption,
        private change: ViewRenderer,
        private data: dataService,
        private _i18n: i18nSupport,
    ){
        // nothing to do
    }

    /* access to data members */

    private get option(): ProductOptView | null {
        if (this.ticketoption.option){
            return this.data.alives.get(this.ticketoption.option) as ProductOptView;
        }

        return null;
    }

    /* access to calculated values */

    get _uuid(): string | null {
        return this.option?._uuid || null;
    }

    get name(): string | null {
        if (this.option){
            return this._i18n.tr(this.option.name);
        }

        return null;
    }

    get available(): boolean {
        if (this.option){
            return this.option.IsAvailable;
        }

        return false;
    }

    get charge(): PriceType | null {
        if (this.option){
            return this.option.priceinfo;
        }

        return null;
    }    
}

/****************************/
/* UPP-PRODUCT-TICKET       */
/****************************/

@Component({
    selector: 'upp-product-ticket',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './upp-ticket.html',
    styleUrls: [
      './upp-ticket.scss'
    ],
    providers: [{
        provide: ViewRenderer,
        useFactory: (change: ChangeDetectorRef) => new ViewRenderer(change),
        deps: [ChangeDetectorRef]
    }]       
})
export class UppProductTicketComponent extends i18nSupport implements OnChanges, OnDestroy {
    @Input() product: TicketProductView | null = null;
    @Input() mode: 'EDIT' | 'VIEW' = 'VIEW';
    @Input() expand = false;

    @Output() ToPanel = new EventEmitter<PlacePanel>();
    @Output() Updated = new EventEmitter<void>();
    @Output() OnSlide = new EventEmitter<{ hash: string; open: boolean }>();
    @Output() OnUnits = new EventEmitter<TicketUnits>();

    private _product_subscription: Subscription | null = null;
    
    private _slidingtimer: ReturnType<typeof setTimeout> | null = null;
    private _slidingphash: string | null = null;
    private _slidinginuse = false;

    constructor(private lang: languageService, private change: ViewRenderer, private alertCtrl: alertService, private data: dataService, public view: viewService) {
        super(lang, change.cdref);
    }

    @HostBinding('class.mobile')
    get IsMobile(): boolean {
        return this.view.Mobile === true;
    }

    ngOnChanges(){
        const prevHash = this._slidingphash;
        const nextHash = this.product?._hash ?? null;

        if (prevHash !== null && prevHash !== nextHash){
            this.OnSlide.emit({ hash: prevHash, open: false });
        }
        else if (nextHash !== null && prevHash === nextHash && this._slidinginuse){
            this.OnSlide.emit({ hash: nextHash, open: false });
        }

        this._slidingphash = nextHash;
        this._slidinginuse = false;
        this.ShowKeyboard = false;

        if (!this.expand){
            this._checked.clear();
        }

        const display = this.display;
        if (display){
            display.expand = this.expand;
        }

        if (this.product){
            this.OnUnits.emit({
                product: this.product,
                units: this.expand ? this._checked.size : 0
            });
        }

        if (this._product_subscription){
            this._product_subscription.unsubscribe();
            this._product_subscription = null;
        }

        if (this.product){
            this._product_subscription = this.product.OnViewRefresh.subscribe(
            () => {
                if (this.display){
                    this.display.markForCheck();
                }
            });
        }
    }

    ngOnDestroy(): void {
        super.OnDestroy();

        if (this._slidingtimer !== null){
            clearTimeout(this._slidingtimer);
            this._slidingtimer = null;
        }

        if (this._slidingphash !== null){
            this.OnSlide.emit({ hash: this._slidingphash, open: false });
        }

        this._slidinginuse = false;

        if (this._product_subscription){
            this._product_subscription.unsubscribe();
            this._product_subscription = null;
        }
    }   
    
    /********************************/
    /* DISPLAY OPTIONS              */
    /********************************/    
    
    OptionTrack(index: number, option: TicketOptionDisplay): any {
        return option?._uuid || null;
    }

    private _display: TicketProductDisplay | null = null; 
    get display() : TicketProductDisplay | null {
        if ((this.product == null) || !this.product.Display){
            return null;
        }
        
        if (this._display === null){
            this._display = new TicketProductDisplay(this.product, this.change, this.data, this);
        }

        return this._display;
    }

    private _showKeyboard = false;
    get ShowKeyboard(): boolean {
        return this._showKeyboard;
    }

    set ShowKeyboard(value: boolean){
        this._showKeyboard = value;
        this.change.markForCheck();
    }

    private _showoptions = false;
    get ShowOptions(): boolean {
        return this._showoptions;
    }

    set ShowOptions(value: boolean){
        this._showoptions = value;
        this.change.markForCheck();
    }

    OnShowOptions(){
        this.ShowOptions = !this.ShowOptions;
    }

    EventColor(status: TicketProductStatus){
        switch(status){
            case 'AC':
            case 'PP': return 'medium';  
            case 'RD':
            case 'PD': return 'success';
        }
        
        return 'dark';        
    }

    get CanAdd(): boolean {
        if (!this.product || !this.product.product){
            return false;
        }

        if (!this.product.product.IsAvailable){
            return false;
        }

        return this.product.options.every(option => {
            if (option){
                return option.IsAvailable;
            }
            return false;
        });
    }

    get CanDel(): boolean {
        if (!this.product || !this.product.amount){
            return false;
        }

        return true;
    }

    get CanUdt(): boolean {
        if (!this.product || !this.product.product){
            return false;
        }

        return this.product.product.IsVarious !== true;
    }

    /********************************/
    /* EVENT HANDLERS               */
    /********************************/
    
    private _checked = new Set <number> ();

    get SelectedUnits(): number {
        return this._checked.size;
    }

    get ExpandUnits(): number[] {
        const amt = this.display?.amount;
        if (amt == null || !Number.isFinite(amt) || amt <= 0) {
            return [];
        }

        return Array.from({ length: Math.ceil(amt) }, (_, i) => i);
    }

    IsChecked(index: number): boolean{
        return this._checked.has(index);
    }

    OnCheckAll(){
        if (this.product == null){
            return;
        }

        const _checked = this._checked.size > 0;
        if (_checked){
            this._checked.clear();
        }
        else{
            for (let _index = 0; _index < (this.product.amount || 0); _index++){
                this._checked.add(_index);
            }
        }

        this.change.markForCheck();
        this.OnUnits.emit({
            product: this.product,
            units: this.expand ? this._checked.size : 0
        });
    }

    OnCheckAny(index: number){
        if (this._checked.has(index)){
            this._checked.delete(index);
        }
        else {
            this._checked.add(index);
        }

        this.change.markForCheck();
        if (this.product){
            this.OnUnits.emit({
                product: this.product,
                units: this.expand ? this._checked.size : 0
            });
        }
    }

    OnAddProduct(event: Event){
        event.stopPropagation();

        if (!this.product || !this.product.ticket){
            return;
        }

        this.product.ticket.AddOneOf(this.product);
        this.Updated.emit();
    }

    private _DelProduct(){
        if (!this.product || !this.product.ticket){
            return;
        }

        this.product.ticket.DelOneOf(this.product);
        this.Updated.emit();
    }

    async OnDelProduct(event: Event){
        event.stopPropagation();

        if (!this.product || !this.product.ticket || !this.product.product){
            return;
        }
        
        let _cartamount = 0;
        for (const product of this.product.ticket.products){
            if (product.product == this.product.product){
                _cartamount += product.amount;
            }
        }

        if (_cartamount == 1){
            const alert = this.alertCtrl.alert({
                header: this.lang.tr('@ticket_remove_product_confirm_tittle'),
                message: this.lang.tr('@ticket_remove_product_confirm_message', [ this.tr(this.product.product.name) ]),
                backdropDismiss: false,
                buttons: [{
                    text: this.lang.tr('@yes'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(true);
                        }, 100);
                        
                        return false;
                    }
                }, {
                    text: this.lang.tr('@no'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(false);
                        }, 100);
                        return false;
                    }
                }]            
            });
    
            alert.onDidDismiss().then((detail:any) => {
                if (detail.data == true){
                    this._DelProduct();
                }
            });
    
            await alert.present();
        }
        else {
            this._DelProduct();
        }
    }

    OnDelSplitter(event: Event){
        event.stopPropagation();

        if (this.product){
            this.product.status = 'DE';
        }

        this.Updated.emit();
    }

    OnShowProduct(event?: Event, slider?: IonItemSliding): void {
        event?.stopPropagation();
        if (slider){
            this._closeslider(slider);
        }

        if (!this.CanUdt || !this.product){
            return;
        }

        this.ToPanel.emit({
            scnd: 'product',
            args: {
                ticket: this.product.ticket,
                product: this.product.product,
                options: this.product.options,
                ticketproduct: this.product
            },
            force: true
        });
    }

    /********************************/
    /* SLIDER CONTROL               */
    /********************************/

    private _sliding = false;

    private get SlideOpen(): boolean {
        return this._slidinginuse;
    }

    private set SlideOpen(open: boolean) {
        if (this.mode !== 'EDIT'){
            open = false;
        }

        const hash = this.product?._hash;
        if (!hash){
            return;
        }

        if (this._slidinginuse === open){
            return;
        }

        this._slidinginuse = open;
        this.OnSlide.emit({ hash, open });
        this.change.markForCheck();
    }

    private _ScheduleSlideStateSync(slider: IonItemSliding): void {
        if (this._slidingtimer !== null){
            clearTimeout(this._slidingtimer);
        }

        this._slidingtimer = setTimeout(() => {
            this._slidingtimer = null;
            this._sliding = false;
            void this._SyncSlideOpen(slider);
        }, 180);
    }

    private async _SyncSlideOpen(slider: IonItemSliding): Promise<void> {
        const amount = await slider.getOpenAmount();
        const open = this.mode === 'EDIT' && Math.abs(amount) > 10;
        this.SlideOpen = open;
    }

    OnSlidingStart(slider: IonItemSliding | null = null): void {
        this._sliding = true;
        if (slider){
            this._ScheduleSlideStateSync(slider);
        }
    }

    OnSlidingDrag(event: CustomEvent<{ amount?: number; ratio?: number }>, slider: IonItemSliding): void {
        this.OnSlidingStart(slider);

        const amount = event.detail?.amount ?? 0;
        const open = this.mode === 'EDIT' && Math.abs(amount) > 10;
        this.SlideOpen = open;
    }

    private _closeslider(slider: IonItemSliding): void {
        const closer = slider.close?.();
        const settled = closer !== undefined && closer !== null && typeof (closer as Promise<void>).finally === 'function'
            ? (closer as Promise<void>)
            : Promise.resolve();

        void settled.finally(() => {
            this.SlideOpen = false;
        });
    }

    OnSlidingClick(event: Event, slider: IonItemSliding): void {
        if (this.mode !== 'EDIT'){
            return;
        }

        if (this._sliding){
            event.stopPropagation();
        }
        else {
            this._closeslider(slider);
        }
    }

    /********************************/
    /* SLIDER ACTIONS               */
    /********************************/

    OpenKeyboard(event: Event, slider: IonItemSliding): void {
        event.stopPropagation();
        this._closeslider(slider);
        if (this.mode !== 'EDIT'){
            return;
        }
        this.ShowKeyboard = true;
    }

    OnKeyboardUpdated(): void {
        this.Updated.emit();
    }

    OnKeyboardClosed(): void {
        this.ShowKeyboard = false;
    }

    OpenProduct(event: Event, slider: IonItemSliding): void {
        this.OnShowProduct(event, slider);
    }

    RemoveAll(event: Event, slider: IonItemSliding): void {
        event.stopPropagation();
        this._closeslider(slider);

        if (!this.product || !this.product.ticket){
            return;
        }

        let line: TicketProductView | null = this.product;
        while (line){
            line.SetLines(0);
            line = line.DisplayFr;
        }

        this.Updated.emit();
    }
}